// Carry-case offer: when the first item goes into an empty cart, offers the rolling travel case once.
// Skipped when the case is already in the cart.
//
// One state at a time, set on the dialog as data-state, and CSS draws each one:
//   closed -> opening -> open -> adding -> added -> closing -> closed
//                          \-> (error) -> open      \-> closing (No thanks / X / Esc / backdrop)
// The page behind stays blurred from the moment the card opens until it has fully faded out.
// Repeat taps mid-step are ignored, and quitting while adding closes at once while the add finishes quietly.
const FADE_MS = 280;
const SUCCESS_HOLD_MS = 1100;

class SCaseOffer extends HTMLElement {
  #controller = null;
  #resolve = null;
  #outcome = 'dismissed';
  #state = 'closed';
  #timer = 0;
  #reduce = window.matchMedia('(prefers-reduced-motion: reduce)');

  connectedCallback() {
    this.#controller = new AbortController();
    const { signal } = this.#controller;
    this.dialog = this.querySelector('dialog');
    if (!this.dialog) return;
    this.dialog.dataset.state = 'closed';

    this.querySelector('[data-case-add]')?.addEventListener('click', (event) => {
      event.preventDefault();
      this.#addCase();
    }, { signal });
    this.dialog.addEventListener('command', (event) => {
      if (event.command === '--dismiss') this.dismiss();
    }, { signal });
    // Esc: run our own fade instead of the browser's instant close.
    this.dialog.addEventListener('cancel', (event) => {
      event.preventDefault();
      this.dismiss();
    }, { signal });
    this.dialog.addEventListener('click', (event) => {
      if (event.target === this.dialog) this.dismiss();
    }, { signal });
    // Safety net: if anything closes the dialog directly, settle the state cleanly.
    this.dialog.addEventListener('close', () => this.#settle(), { signal });

    if (new URLSearchParams(window.location.search).has('case-offer')) this.show();
  }

  disconnectedCallback() {
    this.#controller?.abort();
    window.clearTimeout(this.#timer);
  }

  // True when this add went into an empty cart and the case isn't in it.
  async isEligible(addedVariantId, addedQuantity = 1) {
    if (!this.dialog || this.dataset.designMode === 'true') return false;
    const caseId = String(this.dataset.variantId || '');
    const addedId = String(addedVariantId || '').split('/').pop();
    if (!caseId || addedId === caseId) return false;

    const items = await this.#cartItems();
    if (!items) return false;
    if (items.some((item) => String(item.variant_id) === caseId)) return false;
    // Anything else already in the cart means this wasn't the first add.
    if (items.some((item) => String(item.variant_id) !== addedId)) return false;
    const total = items.reduce((sum, item) => sum + (Number(item.quantity) || 0), 0);
    return total <= (Number(addedQuantity) || 1);
  }

  // Opens with a fade; resolves with 'added' or 'dismissed' once the card has fully closed.
  show() {
    if (!this.dialog || this.#state !== 'closed' || document.querySelector('dialog[open]')) return Promise.resolve();
    this.#outcome = 'dismissed';
    this.#setError(false);
    return new Promise((resolve) => {
      this.#resolve = resolve;
      this.#set('opening');
      this.dialog.showModal();
      // Two frames so the starting styles paint before the visible styles apply.
      requestAnimationFrame(() => requestAnimationFrame(() => {
        if (this.#state === 'opening') this.#set('open');
      }));
    });
  }

  // Quit at any point, including while the case is being added (the add then finishes quietly).
  dismiss() {
    if (this.#state === 'closing' || this.#state === 'closed') return;
    this.#fadeOut();
  }

  async #addCase() {
    if (this.#state !== 'open') return;
    this.#setError(false);
    this.#set('adding');
    this.#announce('adding');

    let ok = false;
    try {
      if (!window.Shopify?.actions?.updateCart) throw new Error('cart unavailable');
      const result = await window.Shopify.actions.updateCart(
        { lines: [{ merchandiseId: this.dataset.variantId, quantity: 1 }] },
        { event: { context: 'product' } }
      );
      ok = !result?.userErrors?.length;
    } catch (e) {
      ok = false;
    }
    if (this.#state !== 'adding') return;

    if (!ok) {
      this.#set('open');
      this.#setError(true);
      return;
    }

    this.#outcome = 'added';
    this.#set('added');
    this.#announce('added');
    document.dispatchEvent(new CustomEvent('mab:cart-bump'));
    window.clearTimeout(this.#timer);
    this.#timer = window.setTimeout(() => this.#fadeOut(), SUCCESS_HOLD_MS);
  }

  #fadeOut() {
    if (this.#state === 'closing' || this.#state === 'closed') return;
    window.clearTimeout(this.#timer);
    this.#set('closing');
    this.#timer = window.setTimeout(() => {
      if (this.dialog.open) this.dialog.close();
      else this.#settle();
    }, this.#reduce.matches ? 0 : FADE_MS);
  }

  #settle() {
    if (this.#state === 'closed') return;
    window.clearTimeout(this.#timer);
    this.#set('closed');
    this.#announce('');
    const resolve = this.#resolve;
    const outcome = this.#outcome;
    this.#resolve = null;
    this.#outcome = 'dismissed';
    resolve?.(outcome);
  }

  #set(state) {
    this.#state = state;
    this.dialog.dataset.state = state;
    const button = this.querySelector('[data-case-add]');
    if (button) {
      const busy = state === 'adding';
      button.setAttribute('aria-busy', String(busy));
      if (busy) button.setAttribute('aria-disabled', 'true');
      else button.removeAttribute('aria-disabled');
    }
  }

  #setError(show) {
    const error = this.querySelector('[data-case-error]');
    if (error) error.hidden = !show;
  }

  #announce(key) {
    const status = this.querySelector('[data-case-status]');
    if (status) status.textContent = key ? status.dataset[key] || '' : '';
  }

  async #cartItems() {
    try {
      const response = await fetch(`${window.Shopify?.routes?.root || '/'}cart.js`, { headers: { Accept: 'application/json' } });
      if (!response.ok) return null;
      const cart = await response.json();
      return cart.items || [];
    } catch (e) {
      return null;
    }
  }
}

if (!customElements.get('s-case-offer')) customElements.define('s-case-offer', SCaseOffer);
